<template>
    <v-dialog :value="value" max-width="1100" scrollable :persistent="busy" @input="$emit('input', $event)">
        <v-card>
            <v-card-title class="text-h6">Import members from CSV</v-card-title>
            <v-card-text>
                <p class="text--secondary mb-4">
                    The file uses the <strong>Export CSV</strong> layout, with the member <strong>ID</strong> in the first column:
                    an empty ID creates a member, an existing ID updates it.
                    <strong>Check</strong> the file first; <strong>Upload</strong> saves it only when every line is valid.
                </p>
                <v-file-input
                    v-model="file"
                    accept=".csv"
                    label="CSV file"
                    prepend-icon=""
                    prepend-inner-icon="mdi-file-delimited-outline"
                    outlined
                    dense
                    show-size
                    :disabled="busy"
                />

                <template v-if="result">
                    <v-alert :type="outcome.type" text dense class="mb-3">{{ outcome.text }}</v-alert>

                    <div v-if="result.rows.length" class="d-flex flex-wrap align-center mb-2 summary">
                        <v-chip small label class="mr-2 mb-1">{{ result.summary.rows }} lines</v-chip>
                        <v-chip small label color="green lighten-4" class="mr-2 mb-1">{{ result.summary.created }} new</v-chip>
                        <v-chip small label color="blue lighten-4" class="mr-2 mb-1">{{ result.summary.updated }} updates</v-chip>
                        <v-chip small label color="red lighten-4" class="mr-2 mb-1">{{ result.summary.errors }} errors</v-chip>
                        <v-chip small label color="orange lighten-4" class="mr-2 mb-1">{{ result.summary.warnings }} warnings</v-chip>
                        <v-spacer />
                        <v-switch
                            v-model="onlyIssues"
                            label="Only errors and warnings"
                            dense
                            hide-details
                            class="mt-0"
                        />
                    </div>

                    <v-data-table
                        v-if="result.rows.length"
                        :headers="headers"
                        :items="visibleRows"
                        item-key="line"
                        dense
                        :items-per-page="50"
                        :footer-props="{ itemsPerPageOptions: [50, 100, -1] }"
                        class="result-table"
                    >
                        <template v-slot:[`item.action`]="{ item }">
                            <v-chip x-small label :color="actions[item.action].color">{{ actions[item.action].text }}</v-chip>
                        </template>
                        <template v-slot:[`item.id`]="{ item }">{{ item.id || '—' }}</template>
                        <template v-slot:[`item.details`]="{ item }">
                            <div v-for="(error, index) in item.errors" :key="'e' + index" class="error--text">
                                <v-icon x-small color="error" class="mr-1">mdi-close-circle</v-icon>{{ error }}
                            </div>
                            <div v-for="(warning, index) in item.warnings" :key="'w' + index" class="warning--text text--darken-2">
                                <v-icon x-small color="warning" class="mr-1">mdi-alert</v-icon>{{ warning }}
                            </div>
                            <div v-if="!item.errors.length" class="text--secondary">{{ item.messages.join(' · ') }}</div>
                        </template>
                    </v-data-table>
                </template>
            </v-card-text>
            <v-card-actions>
                <span class="text-caption text--secondary px-2">
                    Excel drops the leading zeros of card numbers; the import matches them to the existing cards.
                </span>
                <v-spacer />
                <v-btn text :disabled="busy" @click="$emit('input', false)">{{ result && result.saved ? 'Close' : 'Cancel' }}</v-btn>
                <v-btn color="primary" outlined :disabled="!file || saving" :loading="checking" @click="run(true)">
                    <v-icon left>mdi-clipboard-check-outline</v-icon>Check
                </v-btn>
                <v-btn color="primary" depressed :disabled="!canUpload || checking" :loading="saving" @click="run(false)">
                    <v-icon left>mdi-upload</v-icon>Upload
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
    import memberService from '@/services/member-service'

    export default {
        name: 'MemberImportDialog',
        props: {
            value: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                file: null,
                checking: false,
                saving: false,
                // { dryRun, saved, message, summary: { rows, created, updated, errors, warnings }, rows: [{ line, id, name, action, errors, warnings, messages }] }
                result: null,
                onlyIssues: false,
                headers: [
                    { text: 'Line', value: 'line', width: 70 },
                    { text: '', value: 'action', width: 80 },
                    { text: 'ID', value: 'id', width: 80 },
                    { text: 'Name', value: 'name', width: 200 },
                    { text: 'Details', value: 'details', sortable: false }
                ],
                actions: {
                    create: { text: 'New', color: 'green lighten-4' },
                    update: { text: 'Update', color: 'blue lighten-4' },
                    error: { text: 'Error', color: 'red lighten-4' }
                }
            }
        },
        computed: {
            busy() {
                return this.checking || this.saving
            },
            // only a file that passed the check, with nothing changed since
            canUpload() {
                const result = this.result
                return !!result && result.dryRun && !result.message && result.summary.errors === 0 && result.summary.rows > 0
            },
            visibleRows() {
                const rows = this.result ? this.result.rows : []
                return this.onlyIssues ? rows.filter(row => row.errors.length || row.warnings.length) : rows
            },
            outcome() {
                const { message, saved, dryRun, summary } = this.result
                if (message) {
                    return { type: 'error', text: message }
                }
                if (saved) {
                    return { type: 'success', text: `Saved: ${summary.created} members created, ${summary.updated} updated.` }
                }
                if (!summary.rows) {
                    return { type: 'warning', text: 'The file has no member lines.' }
                }
                if (summary.errors) {
                    return { type: 'warning', text: `${summary.errors} of ${summary.rows} lines have errors. Fix them and check the file again; nothing can be uploaded until every line is valid.` }
                }
                return {
                    type: 'info',
                    text: `Check passed: ${summary.created} new members, ${summary.updated} updates` +
                        (summary.warnings ? `, ${summary.warnings} lines with warnings` : '') +
                        `. Nothing has been saved yet${dryRun ? ' — press Upload to save.' : '.'}`
                }
            }
        },
        watch: {
            value(open) {
                if (open) {
                    this.file = null
                    this.result = null
                    this.onlyIssues = false
                }
            },
            file() {
                this.result = null
            }
        },
        methods: {
            run(dryRun) {
                const formData = new FormData()
                formData.append('file', this.file)
                const flag = dryRun ? 'checking' : 'saving'
                this[flag] = true
                memberService.importFromFile(formData, dryRun).then(resp => {
                    this.result = resp.data
                    this.onlyIssues = this.result.summary.errors > 0
                    if (this.result.saved) {
                        this.$notify.success(`Members imported: ${this.result.summary.created} created, ${this.result.summary.updated} updated`)
                        this.$emit('imported')
                    }
                }).catch(error => {
                    this.$notify.error(error, dryRun ? 'The check failed' : 'The upload failed')
                }).finally(() => {
                    this[flag] = false
                })
            }
        }
    }
</script>

<style scoped>
.summary .v-input--switch {
    flex: 0 0 auto;
}

.result-table >>> td {
    vertical-align: top;
    padding-top: 6px !important;
    padding-bottom: 6px !important;
    font-size: 0.8rem !important;
}
</style>
